import {Icon,type IconName} from './Icon';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { formatDateTime } from '../dates';
import { isApiError, type ApiError } from '../api-error';
import {
  compatibilityFailureState,
  saveFailureState,
  stateLiveSemantics,
  type ApplicationState,
  type StateAction,
} from './state-contract';

export {
  APPLICATION_STATE_KINDS,
  compatibilityFailureState,
  reviewedApplicationError,
  stateLiveSemantics,
} from './state-contract';
export type {
  ApplicationState,
  ApplicationStateKind,
  ApplicationStateTone,
  StateAction,
  StateAdvancedDetail,
  StateLiveSemantics,
  ReviewedApplicationError,
  StateRetry,
} from './state-contract';

export type StateProps = ApplicationState & Readonly<{
  className?: string;
  focusOnMount?: boolean;
  headingLevel?: 1 | 2 | 3;
}>;

type StateViewProps = StateProps & Readonly<{
  legacyContent?: ReactNode;
  visuallyHideHeading?: boolean;
}>;

const headingTags = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
} as const;

const stateIcons: Record<ApplicationState['kind'], IconName> = {
  loading: 'clock',
  empty: 'info',
  'no-results': 'search',
  'not-configured': 'info',
  'information-incomplete': 'circle-question-mark',
  'setup-incomplete': 'triangle-alert',
  partial: 'triangle-alert',
  unavailable: 'circle-question-mark',
  'access-denied': 'shield-check',
  'not-found': 'circle-question-mark',
  error: 'circle-alert',
};

function ActionControl({ action, secondary = false }: { action: StateAction; secondary?: boolean }) {
  const className = secondary ? 'button secondary' : 'button';
  if (action.href !== undefined) {
    // Recovery actions stay in the tab sequence even when WebKit's platform
    // preference omits ordinary links from keyboard navigation.
    return <a className={className} href={action.href} tabIndex={0}>{action.label}</a>;
  }
  return <button className={secondary ? 'secondary' : undefined} type="button" disabled={action.disabled} onClick={action.onClick}>{action.label}</button>;
}

function StateView({
  advancedDetail,
  className,
  focusOnMount = false,
  heading,
  headingLevel = 1,
  kind,
  lastKnownAt,
  message,
  primaryAction,
  retry,
  secondaryAction,
  supportReference,
  tone,
  legacyContent,
  visuallyHideHeading = false,
}: StateViewProps) {
  const rootRef = useRef<HTMLElement>(null);
  const generatedId = useId();
  const headingId = `${generatedId}-heading`;
  const messageId = `${generatedId}-message`;
  const lastKnownId = `${generatedId}-last-known`;
  const supportReferenceId = `${generatedId}-support-reference`;
  const semantics = stateLiveSemantics(kind);
  const Heading = headingTags[headingLevel];
  const describedBy = [
    messageId,
    lastKnownAt ? lastKnownId : undefined,
    supportReference ? supportReferenceId : undefined,
  ].filter((value): value is string => value !== undefined).join(' ');

  useEffect(() => {
    if (focusOnMount) rootRef.current?.focus();
  }, [focusOnMount, kind]);

  return <section
    ref={rootRef}
    className={['panel', 'application-state', `application-state--${kind}`, `application-state--${tone}`, className].filter(Boolean).join(' ')}
    role={semantics.role}
    aria-live={semantics.live}
    aria-atomic="true"
    aria-busy={semantics.busy}
    aria-labelledby={headingId}
    aria-describedby={describedBy}
    tabIndex={-1}
    data-state={kind}
    data-tone={tone}
  >
    <span className="application-state__icon" aria-hidden="true"><Icon name={stateIcons[kind]} size={20}/></span>
    <div className="application-state__body">
      <Heading id={headingId} className={visuallyHideHeading ? 'sr-only' : undefined}>{heading}</Heading>
      <div id={messageId} className="application-state__message">{legacyContent ?? message}</div>
      {lastKnownAt && <p id={lastKnownId} className="application-state__last-known">Last known update: <time dateTime={lastKnownAt}>{formatDateTime(lastKnownAt, 'Time unavailable')}</time></p>}
      {supportReference && <p id={supportReferenceId} className="application-state__reference">Support reference: <code>{supportReference}</code></p>}
      {advancedDetail && <details className="application-state__advanced">
        <summary>{advancedDetail.summary}</summary>
        {advancedDetail.description && <p>{advancedDetail.description}</p>}
        {advancedDetail.items && advancedDetail.items.length > 0 && <dl>{advancedDetail.items.map((item) => <div key={`${item.label}:${item.value}`}><dt>{item.label}</dt><dd>{item.value}</dd></div>)}</dl>}
      </details>}
    </div>
    {(primaryAction || retry || secondaryAction) && <div className="actions application-state__actions">
      {primaryAction && <ActionControl action={primaryAction} />}
      {secondaryAction && <ActionControl action={secondaryAction} secondary />}
      {retry && <button className={primaryAction || secondaryAction ? 'secondary' : undefined} type="button" disabled={retry.disabled} onClick={retry.onRetry}>{retry.label ?? 'Try again'}</button>}
    </div>}
  </section>;
}

export function State(props: StateProps) {
  return <StateView {...props} />;
}

export type LoadingProps = Readonly<{
  heading?: string;
  message?: string;
}>;

export function Loading({ heading = 'Loading', message = 'Loading…' }: LoadingProps = {}) {
  return <StateView
    kind="loading"
    tone="information"
    heading={heading}
    message={message}
    headingLevel={2}
    visuallyHideHeading
  />;
}

export type ErrorStateProps = Readonly<{
  error: unknown;
  focusOnMount?: boolean;
  headingLevel?: 1 | 2 | 3;
  onRetry?: () => void;
  subject?: string;
  supportReference?: string;
}>;

function returnToPreviousPage() {
  window.history.back();
}

export function applicationStateForApiError(
  error: ApiError,
  surface: 'information' | 'page' = 'information',
): ApplicationState {
  const common = error.supportReference ? { supportReference: error.supportReference } : {};
  if (error.code === 'access_denied') {
    return {
      kind: 'access-denied',
      tone: 'danger',
      heading: 'You do not have access to this page',
      message: error.message,
      primaryAction: { label: 'Return home', href: '/' },
      secondaryAction: { label: 'Go back', onClick: returnToPreviousPage },
      ...common,
    };
  }
  if (error.code === 'not_found') {
    return {
      kind: 'not-found',
      tone: 'neutral',
      heading: 'Page not found',
      message: 'The link may be out of date or outside your access.',
      primaryAction: { label: 'Return home', href: '/' },
      secondaryAction: { label: 'Search', href: '/search' },
      ...common,
    };
  }
  if (error.code === 'service_unavailable') {
    return {
      kind: 'unavailable',
      tone: 'unavailable',
      heading: surface === 'page' ? 'This page is temporarily unavailable' : 'This information is temporarily unavailable',
      message: error.message,
      ...common,
    };
  }
  if (error.code === 'authentication_required') {
    return {
      kind: 'unavailable',
      tone: 'warning',
      heading: 'Your session has ended',
      message: 'Sign in again to continue.',
      primaryAction: { label: 'Return to sign in', href: '/' },
      ...common,
    };
  }
  return {
    kind: 'error',
    tone: 'danger',
    heading: surface === 'page' ? 'This page could not be opened' : 'Unable to load this information',
    message: error.message,
    ...common,
  };
}

export function ErrorState({
  error,
  focusOnMount = false,
  headingLevel = 2,
  onRetry,
  subject,
  supportReference,
}: ErrorStateProps) {
  if (isApiError(error) && error.operation === 'write' && error.code !== 'authentication_required') {
    const failure = saveFailureState(error);
    const retry = onRetry && error.code !== 'access_denied' && error.code !== 'not_found' ? { retry: { onRetry } } : {};
    return <State {...failure} headingLevel={headingLevel} focusOnMount={focusOnMount} {...retry} {...(supportReference ? { supportReference } : {})}/>;
  }
  const baseFailure = isApiError(error) ? applicationStateForApiError(error) : compatibilityFailureState(error);
  const failure = subject && !(isApiError(error) && error.code === 'authentication_required')
    && (baseFailure.kind === 'unavailable' || baseFailure.kind === 'error')
    ? {
        ...baseFailure,
        heading: baseFailure.kind === 'unavailable'
          ? `${subject} is temporarily unavailable`
          : `${subject} could not be loaded`,
      }
    : baseFailure;
  const retry = onRetry && failure.kind !== 'access-denied' && failure.kind !== 'not-found'
    && !(isApiError(error) && error.code === 'authentication_required')
    ? { onRetry }
    : undefined;
  return <State
    {...failure}
    headingLevel={headingLevel}
    focusOnMount={focusOnMount}
    {...(retry ? { retry } : {})}
    {...(supportReference ? { supportReference } : {})}
  />;
}

export type EmptyProps = Readonly<{
  body?: string;
  children?: ReactNode;
  title?: string;
}>;

export function Empty({ children, title, body }: EmptyProps) {
  const message = body ?? (typeof children === 'string' || typeof children === 'number'
    ? String(children)
    : 'No information is available for this view.');
  return <StateView
    kind="empty"
    tone="neutral"
    heading={title ?? 'No information to show'}
    message={message}
    headingLevel={2}
    visuallyHideHeading={!title}
    className="empty"
    legacyContent={children}
  />;
}
